/**
 * OmegaRevenuePage — /omega/revenue (S51 Wave 2).
 *
 * Founder-only (FounderGuard via founderRoute). English only, mobile-responsive
 * from 375px. Mirrors the S47/S48 design system (cards, human text, apiFetch,
 * useQuery, cn). The Founder OBSERVES the autonomous revenue loop here.
 *
 * Reads GET /api/v1/admin/revenue/summary (S51 Wave 2 backend). Every number is
 * paired with a human sentence — no bare figures. Subscriptions and the
 * autonomous-actions feed are later S51 waves (shown as "coming" placeholders).
 */

import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/api/http';

// ── Wire types (backend contract) ────────────────────────────────────────────

interface ProductRevenue {
  product_slug: string;
  revenue_usd_cents: number;
}

interface RevenueSummary {
  revenue_today_usd_cents: number;
  revenue_month_usd_cents: number;
  revenue_total_usd_cents: number;
  by_product: ProductRevenue[];
  active_customers: number;
  generated_at?: string;
  summary_text: string;
}

function fmtUsd(cents: number): string {
  return '$' + (cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex min-w-[150px] flex-1 flex-col gap-1 rounded-lg border border-border bg-canvas p-4">
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="text-2xl font-semibold tabular-nums text-ink">{value}</div>
      {hint && <div className="text-[11px] text-ink-muted">{hint}</div>}
    </div>
  );
}

export function OmegaRevenuePage() {
  const { data, isPending, isError } = useQuery({
    queryKey: ['omega', 'revenue', 'summary'],
    queryFn: () => apiFetch<RevenueSummary>('/admin/revenue/summary'),
    refetchInterval: 30_000,
    staleTime: 20_000,
    retry: false,
  });

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Revenue</h1>
        <p className="text-sm text-ink-muted">
          The autonomous revenue loop, at a glance. OMEGA sells; you observe.
        </p>
      </header>

      {isPending && <p className="text-sm text-ink-muted">Loading revenue…</p>}

      {isError && (
        <div className="rounded-xl border border-dashed border-border bg-surface p-6 text-center">
          <p className="text-sm text-ink-muted">
            No revenue summary available yet. Once the finance store has records it appears here.
          </p>
        </div>
      )}

      {!isPending && !isError && data && (
        <>
          <section className="flex flex-wrap gap-3">
            <StatCard label="Today" value={fmtUsd(data.revenue_today_usd_cents)} />
            <StatCard label="This month" value={fmtUsd(data.revenue_month_usd_cents)} />
            <StatCard label="All time" value={fmtUsd(data.revenue_total_usd_cents)} />
            <StatCard
              label="Customers"
              value={data.active_customers.toLocaleString()}
              hint="onboarded"
            />
          </section>

          {/* Human explanation — never a bare number (S42 R2-3d pattern). */}
          {data.summary_text && <p className="text-sm text-ink-muted">{data.summary_text}</p>}

          <section className="flex flex-col gap-3">
            <h2 className="font-mono text-xs font-semibold uppercase tracking-wider text-ink-subtle">
              Revenue by product
            </h2>
            <div className="rounded-lg border border-border bg-canvas p-4">
              {data.by_product.length === 0 ? (
                <p className="text-sm text-ink-muted">No product has generated revenue yet.</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {data.by_product
                    .slice()
                    .sort((a, b) => b.revenue_usd_cents - a.revenue_usd_cents)
                    .map((p) => (
                      <div key={p.product_slug} className="flex items-center justify-between gap-3">
                        <span className="truncate text-sm text-ink">{p.product_slug}</span>
                        <span className="shrink-0 font-mono text-sm tabular-nums text-ink-muted">
                          {fmtUsd(p.revenue_usd_cents)}
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="font-mono text-xs font-semibold uppercase tracking-wider text-ink-subtle">
              Active subscriptions &amp; autonomous actions
            </h2>
            <div className="rounded-lg border border-dashed border-border bg-surface p-4">
              <p className="text-sm text-ink-muted">
                Subscription tracking and the autonomous-actions feed (what OMEGA sold, published, and
                marketed) arrive in a later S51 wave, alongside Stripe live activation.
              </p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default OmegaRevenuePage;
